import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import _ from "lodash";
import validator from "validator";

import { ReactComponent as CloseIcon } from "../../Assets/iconsv2/generic/closeBtn.svg";
import { useAuthService } from "../../Context/AuthContext";
import OtherParticipant from "../Meetings/OtherParticipant";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import { useNotify } from "../../Helpers/Notifications";
import SearchMenu from "../GenericComponents/SearchMenu";
import FoundContactElement from "../Chat/FoundContactElement";
import {
  CONFERENCE_ROLE_ORGANIZER,
  CONFERENCE_ROLE_PRESENTER,
  CONFERENCE_ROLE_ATTENDEE,
  CONFERENCE_REGISTRATION_STATUS_APPROVED,
  CONFERENCE_REGISTRATION_STATUS_DENIED,
  CONFERENCE_REGISTRATION_STATUS_PENDING,
} from "../../Constants/constantValues";

const ConferenceParticipants = ({
  selectedParticipants = [],
  initialParticipants = [],
  setSelectedParticipants = () => {},
  handleBackBtn,
  handleSaveBtn,
  otherParticipants = [],
  setOtherParticipants,
  initialOtherParticipants = [],
  activeRole,
  contacts = {},
  registrations = [],
  registrationsLoading = false,
  registrationActionsLoading = {},
  onApproveRegistration = () => {},
  onDenyRegistration = () => {},
  onUninviteRegistration = () => {},
}) => {
  const { notifyError } = useNotify();
  const { t } = useTranslate();
  const { user } = useAuthService();

  const [participantEmail, setParticipantEmail] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [confirmDisabled, setConfirmDisabled] = useState(true);

  const normalizeEmail = (value) => (value ? String(value).trim().toLowerCase() : "");

  // Helpers
  const getOrganizerId = (participant) => {
    if (!participant) return "";
    return String(participant.id || "");
  };

  const getInviteeIdentifier = (participant) => {
    if (typeof participant === "string") return normalizeEmail(participant);
    if (!participant) return "";
    if (participant.email) return normalizeEmail(participant.email);
    return participant.id || participant.name || "";
  };

  const getPresenterIdentifier = (participant) => {
    if (!participant) return "";
    return participant.id || participant.email || participant.name || "";
  };

  const formatStatusLabel = (status) => {
    if (!status) return "";
    return `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
  };

  const filteredOnAppContacts =
    contacts?.on_app_contacts?.filter((item) => item.id !== user?.id) || [];
  const inviteeContacts = useMemo(
    () => filteredOnAppContacts.filter((contact) => normalizeEmail(contact.email)),
    [filteredOnAppContacts]
  );
  const existingInviteeEmails = useMemo(() => {
    const emails = new Set();
    otherParticipants.forEach((participant) => {
      const email = normalizeEmail(getInviteeIdentifier(participant));
      if (email) emails.add(email);
    });
    registrations.forEach((registration) => {
      const email = normalizeEmail(registration?.email);
      if (email) emails.add(email);
    });
    return emails;
  }, [otherParticipants, registrations]);
  const availableInviteeContacts = useMemo(
    () =>
      inviteeContacts.filter(
        (contact) => !existingInviteeEmails.has(normalizeEmail(contact.email))
      ),
    [existingInviteeEmails, inviteeContacts]
  );

  const isCurrentOrganizer = (participant) => {
    if (!user || activeRole !== CONFERENCE_ROLE_ORGANIZER) return false;
    const participantId = getOrganizerId(participant);
    return participantId && String(participantId) === String(user.id);
  };

  const isInviteeSelected = (contact) => {
    const identifier = normalizeEmail(contact?.email);
    if (!identifier) return false;
    return otherParticipants.some(
      (participant) => String(getInviteeIdentifier(participant)) === String(identifier)
    );
  };

  const conferenceContacts = useMemo(() => {
    if (activeRole === CONFERENCE_ROLE_ATTENDEE) return [];
    const merged = [
      ...new Map(
        [...filteredOnAppContacts, ...selectedParticipants, ...initialParticipants].map(
          (item) => [getPresenterIdentifier(item), item]
        )
      ).values(),
    ];
    if (activeRole === CONFERENCE_ROLE_ORGANIZER && user) {
      const withoutCurrent = merged.filter((item) => getOrganizerId(item) !== String(user.id));
      return [user, ...withoutCurrent];
    }
    if (activeRole === CONFERENCE_ROLE_PRESENTER && user) {
      const hasCurrent = merged.some((item) => getPresenterIdentifier(item) === getPresenterIdentifier(user));
      if (!hasCurrent) {
        return [user, ...merged];
      }
    }
    return merged;
  }, [activeRole, filteredOnAppContacts, initialParticipants, selectedParticipants, user]);

  const isContactSelected = (contact) =>
    selectedParticipants.some((item) => getPresenterIdentifier(item) === getPresenterIdentifier(contact));

  const handleAddOtherParticipant = (participant) => {
    const normalizedParticipant = normalizeEmail(participant);
    if (!normalizedParticipant) {
      notifyError(t("conferences.email_required_for_invite"));
      return;
    }

    const allSelected = selectedParticipants.concat(initialParticipants);
    const exists =
      otherParticipants.some(
        (item) => normalizeEmail(getInviteeIdentifier(item)) === normalizedParticipant
      ) ||
      allSelected.find(
        (item) => normalizeEmail(getPresenterIdentifier(item)) === normalizedParticipant
      );

    if (exists) {
      notifyError(t("conferences.email_already_in_list"));
    } else {
      if (activeRole === CONFERENCE_ROLE_ATTENDEE) {
        setOtherParticipants((prev) => [...prev, normalizedParticipant]);
      } else {
        setSelectedParticipants((prev) => [
          ...prev,
          { id: normalizedParticipant, email: normalizedParticipant, name: normalizedParticipant },
        ]);
      }
      setParticipantEmail("");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const trimmed = participantEmail.trim();
      if (validator.isEmail(trimmed)) {
        handleAddOtherParticipant(trimmed);
      }
    }
  };

  const handleRemoveInvitee = (participant) => {
    const identifier = getInviteeIdentifier(participant);
    setOtherParticipants((prev) =>
      prev.filter((item) => getInviteeIdentifier(item) !== identifier)
    );
  };

  const handleSelectInviteeContact = (contact) => {
    const email = normalizeEmail(contact?.email);
    if (!email) {
      notifyError(t("conferences.email_required_for_invite"));
      return;
    }

    const alreadySelected = otherParticipants.some(
      (participant) => String(getInviteeIdentifier(participant)) === String(email)
    );
    const registered = registrations.some(
      (registration) =>
        normalizeEmail(registration?.email) === email
    );

    if (registered && !alreadySelected) {
      notifyError(t("conferences.email_already_in_list"));
      return;
    }

    if (alreadySelected) {
      setOtherParticipants((prev) =>
        prev.filter((participant) => String(getInviteeIdentifier(participant)) !== String(email))
      );
    } else {
      setOtherParticipants((prev) => [...prev, email]);
    }
  };

  const handleSelectContact = (contact) => {
    if (isContactSelected(contact)) {
      if (isCurrentOrganizer(contact)) return;
      setSelectedParticipants((prev) =>
        prev.filter((item) => getPresenterIdentifier(item) !== getPresenterIdentifier(contact))
      );
    } else {
      setSelectedParticipants((prev) => {
        const next = [...prev, contact];
        return next.sort((a, b) => {
          const labelA = String(getPresenterIdentifier(a)).toLowerCase();
          const labelB = String(getPresenterIdentifier(b)).toLowerCase();
          if (labelA > labelB) return 1;
          if (labelA < labelB) return -1;
          return 0;
        });
      });
    }
  };

  const areSetsEqual = (a, b) => {
    if (a.size !== b.size) return false;
    for (const val of a) {
      if (!b.has(val)) return false;
    }
    return true;
  };

  const checkChanges = () => {
    const selectedIds = new Set(selectedParticipants.map((p) => String(getPresenterIdentifier(p))));
    const initialIds = new Set(initialParticipants.map((p) => String(getPresenterIdentifier(p))));

    const contactChanges = areSetsEqual(selectedIds, initialIds);
    const otherChanged = _.isEqual(
      [...otherParticipants].sort(),
      [...initialOtherParticipants].sort()
    );

    if (contactChanges && otherChanged) {
      setConfirmDisabled(true);
    } else {
      setConfirmDisabled(false);
    }
  };

  useEffect(() => {
    checkChanges();
  }, [
    selectedParticipants,
    initialParticipants,
    otherParticipants,
    initialOtherParticipants,
  ]);

  const searchMember = (list) => {
    if (!searchInput) return list;
    const lower = searchInput.toLowerCase();
    return list.filter((item) => {
      const target = (
        item.name ||
        item.email ||
        String(getPresenterIdentifier(item))
      ).toLowerCase();
      return target.includes(lower);
    });
  };

  const searchOtherMember = (list) => {
    if (!searchInput) return list;
    const lower = searchInput.toLowerCase();
    return list.filter((item) => item.toLowerCase().includes(lower));
  };

  const getTitleByRole = () => {
    if (activeRole === CONFERENCE_ROLE_ORGANIZER) {
      return t("conferences.organizer");
    } else if (activeRole === CONFERENCE_ROLE_PRESENTER) {
      return t("conferences.presenters");
    } else if (activeRole === CONFERENCE_ROLE_ATTENDEE) {
      return t("conferences.invitees");
    }
    return t("new_chat.select_participants");
  };

  const getAddByEmailLabel = () => t("conferences.add_by_email", { role: getTitleByRole() });

  const getSelectedParticipantsLabel = () => {
    if (activeRole === CONFERENCE_ROLE_ATTENDEE) {
      return t("general.contacts");
    }
    return t("conferences.selected_participants", { role: getTitleByRole() });
  };

  const getEmailPlaceholder = () => t("conferences.add_participant_email_placeholder", { role: getTitleByRole().toLowerCase() });

  const showRegistrations =
    activeRole === CONFERENCE_ROLE_ATTENDEE &&
    (registrationsLoading ||
      (registrations && registrations.length > 0) ||
      (otherParticipants && otherParticipants.length > 0));

  const combinedInvitees = useMemo(() => {
    const order = {
      [CONFERENCE_REGISTRATION_STATUS_APPROVED]: 0,
      [CONFERENCE_REGISTRATION_STATUS_PENDING]: 1,
      [CONFERENCE_REGISTRATION_STATUS_DENIED]: 2,
    };

    const registrationIds = new Set(registrations.map((r) => normalizeEmail(r.email)));

    const mappedRegistrations = registrations.map((registration) => ({
      ...registration,
      _source: "registration",
    }));

    const mappedSelected = otherParticipants
      .filter((participant) => !registrationIds.has(normalizeEmail(participant)))
      .map((participant) => ({
        id: participant,
        email: participant,
        name: participant,
        status: CONFERENCE_REGISTRATION_STATUS_APPROVED,
        _source: "selected",
      }));

    return [...mappedRegistrations, ...mappedSelected].sort((a, b) => {
      const orderA = order[a.status] ?? 99;
      const orderB = order[b.status] ?? 99;
      if (orderA !== orderB) return orderA - orderB;
      const labelA = (a.name || a.email || "").toLowerCase();
      const labelB = (b.name || b.email || "").toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [otherParticipants, registrations]);

  const renderRegistrationRow = (registration) => {
    const isPending = registration.status === CONFERENCE_REGISTRATION_STATUS_PENDING;
    const isApproved = registration.status === CONFERENCE_REGISTRATION_STATUS_APPROVED;
    const isDenied = registration.status === CONFERENCE_REGISTRATION_STATUS_DENIED;
    const actionLoading = !!registrationActionsLoading?.[registration.id];
    const label = registration.name || registration.email;

    return (
      <RegistrationRow key={registration.id}>
        <RegistrationInfo>
          <RegistrationName>{label}</RegistrationName>
          {registration.email && (
            <RegistrationEmail>{registration.email}</RegistrationEmail>
          )}
        </RegistrationInfo>
        <RegistrationStatus $status={registration.status}>
          {formatStatusLabel(registration.status)}
        </RegistrationStatus>
        <RegistrationActions>
          {isPending && (
            <>
              <RegistrationActionButton
                $positive
                onClick={() => onApproveRegistration(registration)}
                disabled={actionLoading}
              >
                {t("conferences.approve")}
              </RegistrationActionButton>
              <RegistrationActionButton
                $danger
                onClick={() => onDenyRegistration(registration)}
                disabled={actionLoading}
              >
                {t("general.deny")}
              </RegistrationActionButton>
            </>
          )}
          {isApproved && (
            <RegistrationActionButton
              $danger
              onClick={() => onUninviteRegistration(registration)}
              disabled={actionLoading}
            >
              {t("conferences.uninvite")}
            </RegistrationActionButton>
          )}
          {isDenied && (
            <RegistrationActionButton
              $positive
              onClick={() => onApproveRegistration(registration)}
              disabled={actionLoading}
            >
              {t("conferences.approve")}
            </RegistrationActionButton>
          )}
        </RegistrationActions>
      </RegistrationRow>
    );
  };


  const renderApprovedInviteeRow = (participant) => {
    const label = participant.name || participant.email || participant.id || participant || "";
    return (
      <RegistrationRow key={`selected-${getInviteeIdentifier(participant)}`}>
        <RegistrationInfo>
          <RegistrationName>{label}</RegistrationName>
          {participant.email && (
            <RegistrationEmail>{participant.email}</RegistrationEmail>
          )}
        </RegistrationInfo>
        <RegistrationStatus $status={CONFERENCE_REGISTRATION_STATUS_APPROVED}>
          {formatStatusLabel(CONFERENCE_REGISTRATION_STATUS_APPROVED)}
        </RegistrationStatus>
        <RegistrationActions>
          <RegistrationActionButton
            $danger
            onClick={() => handleRemoveInvitee(participant)}
          >
            {t("conferences.uninvite")}
          </RegistrationActionButton>
        </RegistrationActions>
      </RegistrationRow>
    );
  };

  const renderInviteeRow = (invitee) => {
    if (invitee._source === "registration") return renderRegistrationRow(invitee);
    return renderApprovedInviteeRow(invitee);
  };

  return (
    <FullScreenItem>
      <Wrapper>
        <Header>
          <div
            style={{ width: "100%", display: "flex", justifyContent: "start" }}
          ></div>
          <ProfileTitle>
            <Title>{getTitleByRole()}</Title>
          </ProfileTitle>
          <div
            style={{ width: "100%", display: "flex", justifyContent: "end" }}
          >
            <CloseBtn>
              <CloseIcon onClick={handleBackBtn} />
            </CloseBtn>
          </div>
        </Header>
        <CardInputsWrapper>
          <Group style={{ overflow: "unset" }}>
            <Subtitle>
              {t("conferences.search_participant_role", { role: getTitleByRole() })}
            </Subtitle>
            <ChatOption style={{ flexDirection: "column" }}>
              <SearchMenu
                name="searchInput"
                value={searchInput}
                placeholder={t("general.search")}
                setvalue={setSearchInput}
              />
            </ChatOption>
          </Group>

          <Group>
            <Subtitle>{getAddByEmailLabel()}</Subtitle>
            <ChatOption style={{ flexDirection: "column" }}>
              <SearchWrapper>
                <CustomInput
                  value={participantEmail}
                  placeholder={getEmailPlaceholder()}
                  onChange={(e) => setParticipantEmail(e.currentTarget.value)}
                  onKeyDown={handleKeyDown}
                  name="participantEmail"
                  maxLength={100}
                />
                {validator.isEmail(participantEmail.trim()) && (
                  <AddEmailBtn
                    onClick={() => handleAddOtherParticipant(participantEmail.trim())}
                  >
                    {t("general.add")}
                  </AddEmailBtn>
                )}
              </SearchWrapper>
            </ChatOption>
          </Group>

          {activeRole === CONFERENCE_ROLE_ATTENDEE && (
            <Group>
              <Subtitle>{getSelectedParticipantsLabel()}</Subtitle>
              <OptionWrapper>
                {searchMember(availableInviteeContacts).map((participant) => {
                  const key =
                    getInviteeIdentifier(participant) ||
                    participant.id ||
                    participant.email;
                  const label = participant.name || participant.email || "";
                  const email = participant.email;
                  return (
                    <InviteeRow key={key}>
                      <FoundContactElement
                        item={participant}
                        handleSelectContact={() => {}}
                        selected={false}
                        noCheck
                        style={{ paddingRight: 12 }}
                      />
                      <AddInviteeButton
                        type="button"
                        onClick={() => handleSelectInviteeContact(participant)}
                        disabled={!email}
                      >
                        {t("general.add")}
                      </AddInviteeButton>
                    </InviteeRow>
                  );
                })}
              </OptionWrapper>
            </Group>
          )}

          {showRegistrations && (
            <Group>
              <Subtitle>{t("conferences.invitees")}</Subtitle>
              <RegistrationList>
                {registrationsLoading && (
                  <InfoText>{t("conferences.loading_registrations")}</InfoText>
                )}
                {!registrationsLoading && combinedInvitees.length === 0 && (
                  <InfoText>{t("conferences.no_registration_requests")}</InfoText>
                )}
                {!registrationsLoading &&
                  combinedInvitees.length > 0 &&
                  combinedInvitees.map(renderInviteeRow)}
              </RegistrationList>
            </Group>
          )}

          {activeRole !== CONFERENCE_ROLE_ATTENDEE && (
            <Group>
              <Subtitle>{getSelectedParticipantsLabel()}</Subtitle>
              <OptionWrapper>
                {searchMember(conferenceContacts).map((participant) => (
                  <FoundContactElement
                    key={getPresenterIdentifier(participant)}
                    item={participant}
                    handleSelectContact={handleSelectContact}
                    selected={isContactSelected(participant)}
                  />
                ))}
              </OptionWrapper>
            </Group>
          )}
        </CardInputsWrapper>
        <PrimaryButton
          style={{ marginLeft: "auto" }}
          disabled={confirmDisabled}
          onClick={() => handleSaveBtn()}
        >
          {t("general.save")}
        </PrimaryButton>
      </Wrapper>
    </FullScreenItem>
  );
};

export default ConferenceParticipants;

const FullScreenItem = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 25px;
  height: 90%;
  width: 100%;
  max-width: 644px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  gap: 20px;
  border-radius: 4px;
`;

const CardInputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  overflow: auto;
  height: 100%;
`;

const Title = styled.p`
  text-transform: capitalize;
  font-weight: 700;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.04px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 12px;
  font-weight: 500;
  line-height: 22px;
  text-transform: uppercase;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Header = styled.div`
  gap: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const ProfileTitle = styled.div`
  width: 100%;
  justify-content: center;
  display: flex;
  min-width: 155px;
`;

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 4px;
`;

const RegistrationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RegistrationRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 6px;
  background: ${({ theme }) => theme.menu.background};
`;

const RegistrationInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const RegistrationName = styled.span`
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-weight: 600;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RegistrationEmail = styled.span`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RegistrationStatus = styled.span`
  justify-self: end;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme, $status }) => {
    if ($status === CONFERENCE_REGISTRATION_STATUS_APPROVED) {
      return theme.premium_green || theme.valid_green || "#11B973";
    }
    if ($status === CONFERENCE_REGISTRATION_STATUS_DENIED) {
      return (theme.menu && theme.menu.deleteColor) || "#E74C3C";
    }
    return theme.menu.secondaryTextColor;
  }};
  background: ${({ theme }) => theme.menu.groupContentBackground};
  text-transform: capitalize;
`;

const RegistrationActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const RegistrationActionButton = styled.button`
  border: 1px solid
    ${({ theme, $danger, $positive }) => {
      if ($danger) return   "#ec5b5bff";
      if ($positive) return theme.valid_green || "#11B973";
      return theme.sidebar.borderColor;
    }};
  color: ${({ theme, $danger, $positive }) => {
    if ($danger) return "#ec5b5bff";
    if ($positive) return theme.valid_green || "#11B973";
    return theme.menu.primaryTextColor;
  }};
  background: none;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;

const InfoText = styled.p`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 12px;
  margin: 0;
`;

const ChatOption = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
  border-bottom: 1px solid #363e5180;
  &:last-child {
    border: none;
  }
  h1 {
    min-width: 75px;
    color: #ffffff99;
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: -0.40799999237060547px;
  }
  h2 {
    text-transform: capitalize;
    color: ${({ theme }) => theme.menu.primaryTextColor};
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: -0.40799999237060547px;
  }
`;

const CustomInput = styled.input`
  background: none;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-align: left;
  color: ${({ theme }) => theme.menu.primaryTextColor};

  width: 100%;
  border: none;
  &:focus {
    outline: none;
  }
  &::placeholder {
    font-weight: 400;
  }
`;

const AddEmailBtn = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: ${({ theme }) => theme.menu.selectedTextColor};
  cursor: pointer;
`;

const InviteeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
`;

const AddInviteeButton = styled.button`
  border: 1px solid ${({ theme }) => theme.valid_green || "#11B973"};
  color: ${({ theme }) => theme.valid_green || "#11B973"};
  background: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-left: auto;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 8px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 4px;
  gap: 8px;
  background: ${({ theme }) => theme.menu.background};
`;
const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  svg {
    height: 28px;
    width: 28px;
    color: ${({ theme }) => theme.menu.selectedOpionBackground};
    stroke: ${({ theme }) => theme.sidebar.borderColor};
  }
`;
